import type { TFunction } from 'i18next';
import type { EnergySource, RuleRequirement } from '@dashboard/contracts';

/**
 * Translation keys for the fleet's catalog values.
 *
 * The catalogs are keyed in `snake_case` because that is what the wire and the
 * database use; the translation catalog is nested camelCase because that is
 * what every other section of it is. One function does the conversion, in one
 * place, rather than a `replace` regex repeated at each call site -- which is
 * how `fleet.energy.natural_gas` ends up looked up in one component and
 * `fleet.energy.naturalGas` in another, with only one of them finding anything.
 */
export function energyLabelKey(source: EnergySource): string {
  const camel = source.replace(/_(.)/g, (_, letter: string) => letter.toUpperCase());
  return `fleet.energy.${camel}`;
}

/**
 * One sentence of a compliance rule, with the rule's own figures in it.
 *
 * A function for a call that would otherwise be inline, and the reason is how
 * i18next is typed rather than anything about these strings. Given a literal
 * key it resolves an exact return type; given a `string` key *and* values to
 * interpolate -- which is every sentence in a catalog-driven screen -- the
 * hook's own `t` widens to a union including the object `returnObjects` could
 * produce, which React refuses as a child. Passing it through this signature
 * resolves it back to the string it always was, with no cast anywhere.
 *
 * In one place rather than at each of the two call sites, so the reasoning is
 * written once instead of puzzled over twice.
 */
export function ruleSentence(t: TFunction, requirement: RuleRequirement): string {
  return t(requirement.labelKey, requirement.values);
}
